// defineHierarchyChart — a stacked-bar config composable from a declarative level table
// (CHART_GENERALIZATION.md §9, IA_ABSORPTION_PLAN Phase 5 step 15).
//
// Every drill-down config feeding StackedHorizontalBarChart (@rdrive/plugin-chart-stacked-bar)
// repeats the same two query SHAPES per level: a GROUP BY that yields the base chart's row
// contract (`id, parentId?, type?, title, status_title, color, value, sort`) and a
// `SELECT DISTINCT <keys> … WHERE …` that becomes the chart's filter. This builder owns those
// shapes and the bookkeeping around them (the drill-so-far, the drill scope, the reset, the
// navigation config); the app's level table supplies EVERY table, column, join and predicate.
//
// Why a generic SQL template may live in a package (PLATFORM.md §3.2 rule 2 — "packages contain
// zero project SQL"): nothing here names a table, a column or a predicate. What is fixed is the
// skeleton (SELECT … FROM … JOIN … WHERE … GROUP BY … HAVING … ORDER BY) and the row contract's
// aliases, which are the base chart's API, not the project's schema — the same argument that
// put `timeBuckets.js` (date-bucket expressions over a caller-supplied column) and
// `createFilterContext` (a context table over caller-supplied keys) in packages. A level that
// does not fit the shape supplies its own SQL (`sql`, `filterSql`), so an odd level never
// bends the template.
//
// Contract and an example level table: README.md → "defineHierarchyChart".

import { escapeSql } from '@rdrive/helper-sql';
import { makeChartConfig } from './chartConfig.js';

const ROW_SOURCES = ['id', 'parentId', 'type'];

const STATUS_FIELDS = ['title', 'color', 'sort'];

const csv = (v) => (Array.isArray(v) ? v.join(', ') : v);
const isEmpty = (v) => v === null || v === undefined || v === '';
// A select expression that is a literal — a string (`'Issue'`) or a number (`0`) — is a constant:
// never a GROUP BY key (a number there would even be read as a column position), and a constant
// `sort` is selected as it is rather than through MIN (AM_ABSORPTION_PLAN E2)
const isLiteral = (expr) => /^'.*'$/s.test(String(expr).trim()) || /^-?\d+(\.\d+)?$/.test(String(expr).trim());
const unique = (items) => items.filter((v, i) => items.indexOf(v) === i);

/**
 * @typedef {[column: string | ((ctx: SelectContext) => string), from: 'id' | 'parentId' | 'type' | { drill: string, field?: 'id' | 'parentId' | 'type' }]} FilterPair
 *   A generated condition `<column> = '<escaped value>'`. `from` names where the value comes
 *   from: the clicked row's positional `id`/`parentId`/`type`, or `{ drill: '<level>' }` — the
 *   selection made earlier at that (shallower) level (its `id` unless `field` says otherwise).
 *   The value is always passed through `escapeSql`. A function `column` is evaluated per call
 *   (e.g. issue-analysis's `biColumnSql(ctx.filterContext, 'BI-…')`).
 */

/**
 * @typedef {FilterPair | ((ctx: SelectContext) => string | null | undefined)} FilterCondition
 *   A pair (generated, escaped) or a function returning a raw fragment — the escape hatch at
 *   condition granularity: the app owns its escaping there. A function returning
 *   null/undefined/'' adds nothing.
 */

/**
 * @typedef {object} LevelFilter
 * @property {FilterCondition[]} [where] ANDed; no WHERE when nothing is left
 * @property {string[]} [joins] verbatim, after `FROM <table> <alias>`
 * @property {string} [keys] overrides the chart's `filter.keys` for this level
 * @property {string} [table] overrides the chart's `filter.table`
 * @property {string} [alias] overrides the chart's `filter.alias`
 * @property {boolean} [skipEmpty] overrides the chart's `filter.skipEmpty`
 * @property {boolean} [scoped] this level CONSUMES the drill scope (the terminal status level)
 *   instead of setting it — see the chart's `filter.scope`
 * @property {(ctx: SelectContext) => string | Promise<string>} [label] opt-in (AM_ABSORPTION_PLAN
 *   C3): the filter's LABEL — awaited after the filter query is built, its result handed to the
 *   filter context as `updateFilterWithQuery`'s third argument (@rdrive/data-filters: the chip
 *   text `getActiveFilters()` returns). Already translated: the hook owns its strings. Without a
 *   hook the call keeps its two arguments
 */

/**
 * @typedef {object} HierarchyLevel
 * @property {string} level navigationConfig level name (also the key of its provider/updater)
 * @property {string} titleKey raw i18n key — the base chart runs `t()`
 * @property {string} [id] SQL expression → `id` (required unless `sql`)
 * @property {string} [title] SQL expression → `title` (required unless `sql`)
 * @property {string} [parentId] SQL expression → `parentId` (omitted when absent)
 * @property {string} [type] SQL expression → `type` (a literal such as `'Issue'` is
 *   not added to the default GROUP BY)
 * @property {string[]} [joins] verbatim, after `FROM`; the chart's `status.join` is appended
 *   last unless this list already contains it (then it stays where you put it)
 * @property {string} [where] verbatim WHERE predicate
 * @property {string | string[]} [groupBy] default: the distinct non-literal select expressions
 *   `id, parentId, type, title, <status title>, <status color>`
 * @property {'count' | 'window' | string} [value] `'count'` (default) → `COUNT(*)`;
 *   `'window'` → `SUM(COUNT(*)) OVER (PARTITION BY <groupBy>)`; anything else is used verbatim
 * @property {string | string[]} [partitionBy] `SUM(COUNT(*)) OVER (PARTITION BY <this>)`
 * @property {string} [having] verbatim HAVING predicate
 * @property {string | string[]} [orderBy] default `<title>, sort, status_title`
 * @property {LevelFilter | null} [filter] the generated context updater; absent/null → no
 *   updater (a terminal level without one is inert in the base chart)
 * @property {(ctx: DataContext) => string | Promise<string>} [sql] ESCAPE HATCH — the level's
 *   whole data query (must return the row contract); replaces the generated SELECT
 * @property {(ctx: SelectContext) => string | null | Promise<string | null>} [filterSql] ESCAPE
 *   HATCH — the level's whole filter query; replaces the generated one (null → apply nothing).
 *   Scope bookkeeping still applies (a non-`filter.scoped` level's query becomes the scope)
 * @property {(ctx: DataContext) => unknown} [before] awaited before the level's data query
 *   (e.g. building a lookup table the SELECT joins)
 * @property {(ctx: SelectContext) => unknown} [beforeFilter] awaited before the level's
 *   filter query
 * @property {(ctx: SelectContext) => unknown} [onSelect] awaited first when the level's bar is
 *   clicked, before any query (e.g. `ctx.setLevel('status')` to skip a level)
 */

/**
 * @typedef {string | ((ctx: DataContext) => string)} StatusField
 *   A SQL expression for the status title / colour / sort, or a function of the data context
 *   returning one, evaluated for every generated data query (e.g. a colour literal that follows
 *   the theme) — your function, your `escapeSql`
 */

/**
 * @typedef {object} DataContext
 * @property {string} level
 * @property {boolean} filtered the flag the base chart passed (false only for its breadcrumb lookup)
 * @property {string} table the resolved `source.table` for this call
 * @property {string} alias
 * @property {object} conn
 * @property {object} filterContext the live (unref'd) filter context
 * @property {Record<string, { id: unknown, parentId: unknown, type: unknown }>} drill the selections so far
 */

/**
 * @typedef {object} SelectContext
 * @property {string} level
 * @property {unknown} id the clicked row's `id` (the base's 1st positional argument)
 * @property {unknown} parentId the 2nd
 * @property {unknown} type the 3rd
 * @property {Record<string, { id: unknown, parentId: unknown, type: unknown }>} drill the
 *   selection at this level and every shallower one still in force
 * @property {object} conn
 * @property {object} filterContext the live (unref'd) filter context
 * @property {ReturnType<import('./stackedBarChartData.js').makeDrillScope>} scope the drill scope
 * @property {(level: string) => void} setLevel writes the page's `currentLevel` ref
 *   (`v-model:currentLevel`)
 */

/**
 * Build `{ dataProviders, contextUpdaters, navigationConfig }` for StackedHorizontalBarChart
 * from a level table. The package holds the query SHAPE only; every SQL fragment is the app's.
 *
 * Drill state: a click at level k records `{ id, parentId, type }` under k and forgets every
 * selection at k and deeper first (a re-selection starts that branch over); `reset` forgets all.
 * `{ drill: '<level>' }` filter pairs and the hooks read it.
 *
 * @param {object} options
 * @param {string} options.chartName unique per chart INSTANCE (the filter registration's id)
 * @param {object | import('vue').Ref} options.filterContext the filter-context duck type
 * @param {object} options.conn the DuckDB connection
 * @param {import('vue').Ref | null} [options.currentLevel] the page's `v-model:currentLevel` ref,
 *   needed only when a hook calls `setLevel`
 * @param {{ table: string | ((filtered: boolean) => string), alias: string }} options.source
 *   the data queries' FROM — a function receives the base's `filtered` flag
 * @param {{ join?: string, title?: StatusField, color?: StatusField, sort?: StatusField }} options.status
 *   the status join and its columns (defaults `s.title`, `s.color`, `s.sort`). **Status-less**
 *   (opt-in by omission, AM_ABSORPTION_PLAN E2): without `join` nothing is joined and `title`,
 *   `color` and `sort` are required (no `s.*` defaults) — e.g. constants, so every bar is one
 *   segment (`{ title: "'Installation Cost'", color: () => `'${ramp[2]}'`, sort: '0' }`). In either
 *   mode a literal field is left out of the default GROUP BY and a literal `sort` is selected as
 *   it is (not `MIN(…)`)
 * @param {{ table?: string, alias?: string, keys?: string, skipEmpty?: boolean,
 *   scope?: 'andIdIn' | 'replaceSource' | null }} [options.filter] the filter queries' FROM and
 *   SELECT DISTINCT keys (alias defaults to `source.alias`); `skipEmpty` drops a pair whose value
 *   is null/undefined/'' (and the WHERE with no pair left) instead of comparing to `'null'`/`''`;
 *   `scope` makes every non-`scoped` level's filter query the drill scope and narrows the
 *   `scoped` level by it — `'andIdIn'` appends `AND <keys> IN (<scope>)` (single key only),
 *   `'replaceSource'` reads FROM `(<scope>)` instead of the table
 * @param {Partial<HierarchyLevel>} [options.defaults] merged (shallowly) under every level
 * @param {HierarchyLevel[]} options.levels in drill order; the last one is terminal
 * @returns {{ dataProviders: Record<string, (filtered?: boolean) => Promise<object[]>>,
 *   contextUpdaters: Record<string, (id: unknown, parentId: unknown, type: unknown) => Promise<void>>,
 *   navigationConfig: { level: string, titleKey: string }[] }}
 */
export function defineHierarchyChart({
  chartName,
  filterContext,
  conn,
  currentLevel = null,
  source,
  status,
  filter = {},
  defaults = {},
  levels,
}) {
  const table = levels?.map((level) => ({ ...defaults, ...level })) ?? [];
  validate({ chartName, source, status, filter, table });

  const { ctx, run, applyFilter, removeFilter, drillScope } = makeChartConfig({ chartName, filterContext, conn });

  // Status-less (no `status.join`, AM_ABSORPTION_PLAN E2): no join and no `s.*` defaults — the
  // three columns are the config's own (validated above)
  const st = status?.join ? { title: 's.title', color: 's.color', sort: 's.sort', ...status } : { ...status };
  const chartFilter = { alias: source.alias, skipEmpty: false, scope: null, ...filter };
  const depth = new Map(table.map((level, i) => [level.level, i]));

  let drill = {};

  const setLevel = (name) => {
    if (currentLevel && typeof currentLevel === 'object' && 'value' in currentLevel) {
      currentLevel.value = name;
    } else {
      console.warn(`[${chartName}] setLevel('${name}') needs the page's currentLevel ref`);
    }
  };

  // ------------------------------------------------------------------ data side
  // The status columns of one data query: a function field is called with the data context
  const statusSql = (context) => {
    const out = { join: st.join };
    for (const key of STATUS_FIELDS) {
      const field = st[key];
      out[key] = typeof field === 'function' ? field(context) : field;
      if (typeof out[key] !== 'string' || out[key] === '') {
        throw new Error(`defineHierarchyChart(${chartName}): status.${key}(ctx) must return a SQL expression`);
      }
    }
    return out;
  };

  // A literal type / status title / status colour is a constant, not a grouping key
  const keyOf = (expr) => (expr && !isLiteral(expr) ? expr : null);

  const groupBySql = (level, s) =>
    csv(level.groupBy) ??
    unique(
      [level.id, level.parentId, keyOf(level.type), level.title, keyOf(s.title), keyOf(s.color)]
        .filter(Boolean),
    ).join(', ');

  const valueSql = (level, s) => {
    if (level.partitionBy) return `SUM(COUNT(*)) OVER (PARTITION BY ${csv(level.partitionBy)})`;
    const value = level.value ?? 'count';
    if (value === 'count') return 'COUNT(*)';
    if (value === 'window') return `SUM(COUNT(*)) OVER (PARTITION BY ${groupBySql(level, s)})`;
    return value;
  };

  const dataSql = (level, from, s) => {
    const joins = level.joins ?? [];
    const select = [
      `${level.id} AS id`,
      level.parentId && `${level.parentId} AS parentId`,
      level.type && `${level.type} AS type`,
      `${level.title} AS title`,
      `${s.title} AS status_title`,
      `${s.color} AS color`,
      `${valueSql(level, s)} AS value`,
      isLiteral(s.sort) ? `${s.sort} AS sort` : `MIN(${s.sort}) AS sort`,
    ].filter(Boolean);
    return [
      'SELECT',
      `  ${select.join(',\n  ')}`,
      `FROM ${from} ${source.alias}`,
      ...(!s.join || joins.includes(s.join) ? joins : [...joins, s.join]),
      level.where && `WHERE ${level.where}`,
      `GROUP BY ${groupBySql(level, s)}`,
      level.having && `HAVING ${level.having}`,
      `ORDER BY ${csv(level.orderBy) ?? `${level.title}, sort, status_title`}`,
    ]
      .filter(Boolean)
      .join('\n');
  };

  const provider = (level) => async (filtered = true) => {
    const from = typeof source.table === 'function' ? source.table(filtered) : source.table;
    const context = {
      level: level.level, filtered, table: from, alias: source.alias, conn, filterContext: ctx(), drill: { ...drill },
    };
    if (level.before) await level.before(context);
    return run(level.sql ? await level.sql(context) : dataSql(level, from, statusSql(context)));
  };

  // ---------------------------------------------------------------- filter side
  const valueOf = (from, selection) =>
    typeof from === 'string' ? selection[from] : selection.drill[from.drill]?.[from.field ?? 'id'] ?? null;

  const condition = (entry, selection, skipEmpty) => {
    if (typeof entry === 'function') {
      const fragment = entry(selection);
      return isEmpty(fragment) ? null : fragment;
    }
    const [column, from] = entry;
    const value = valueOf(from, selection);
    if (skipEmpty && isEmpty(value)) return null;
    return `${typeof column === 'function' ? column(selection) : column} = '${escapeSql(value)}'`;
  };

  const filterSqlFor = (level, selection) => {
    const f = level.filter;
    const keys = f.keys ?? chartFilter.keys;
    const from = f.table ?? chartFilter.table;
    const scope = f.scoped ? chartFilter.scope : null;
    const skipEmpty = f.skipEmpty ?? chartFilter.skipEmpty;
    const conditions = (f.where ?? []).map((entry) => condition(entry, selection, skipEmpty)).filter(Boolean);
    return [
      `SELECT DISTINCT ${keys}`,
      `FROM ${scope === 'replaceSource' ? drillScope.source(from) : from} ${f.alias ?? chartFilter.alias}`,
      ...(f.joins ?? []),
      conditions.length > 0 && `WHERE ${conditions.join(' AND ')}`,
      scope === 'andIdIn' && drillScope.andIdIn(keys),
    ]
      .filter(Boolean)
      .join('\n');
  };

  const updater = (level) => async (id, parentId, type) => {
    // A selection at this level starts its branch over: forget it and everything deeper
    const index = depth.get(level.level);
    for (const [name, i] of depth) if (i >= index) delete drill[name];
    drill[level.level] = { id, parentId, type };

    const selection = {
      level: level.level, id, parentId, type, drill: { ...drill }, conn, filterContext: ctx(), scope: drillScope, setLevel,
    };
    if (level.onSelect) await level.onSelect(selection);
    if (level.beforeFilter) await level.beforeFilter(selection);

    const sql = level.filterSql ? await level.filterSql(selection) : filterSqlFor(level, selection);
    if (!sql) return;
    // Every non-scoped filter becomes the drill scope the scoped (status) level narrows by
    if (chartFilter.scope && !level.filter?.scoped) drillScope.set(sql);
    // Opt-in label: only a level with a `filter.label` hook passes a third argument
    const label = level.filter?.label ? await level.filter.label(selection) : undefined;
    await applyFilter(sql, label);
  };

  const reset = async () => {
    drill = {};
    drillScope.reset();
    await removeFilter();
  };

  // --------------------------------------------------------------------- result
  const dataProviders = Object.fromEntries(table.map((level) => [level.level, provider(level)]));
  const contextUpdaters = Object.fromEntries(
    table.filter((level) => level.filter || level.filterSql).map((level) => [level.level, updater(level)]),
  );
  contextUpdaters.reset = reset; /* required by the base chart */
  const navigationConfig = table.map(({ level, titleKey }) => ({ level, titleKey }));

  return { dataProviders, contextUpdaters, navigationConfig };
}

// Fail at definition time, with the level's name, rather than with a SQL error at the first click
function validate({ chartName, source, status, filter, table }) {
  const fail = (msg) => {
    throw new Error(`defineHierarchyChart(${chartName}): ${msg}`);
  };
  if (!chartName) fail('chartName is required');
  if (table.length === 0) fail('levels must be a non-empty array');
  const generatedData = table.some((level) => !level.sql);
  if (!source?.table && generatedData) fail('source.table is required');
  if (!source?.alias) fail('source.alias is required');
  // Status-less mode (AM_ABSORPTION_PLAN E2): no join, but then the three columns are the config's
  if (!status?.join && generatedData && !STATUS_FIELDS.every((key) => status?.[key])) {
    fail('status.join is required — or, status-less (no join), status.title, status.color and status.sort');
  }
  if (status?.join != null && typeof status.join !== 'string') fail('status.join must be a JOIN clause');
  for (const key of STATUS_FIELDS) {
    const field = status?.[key];
    if (field !== undefined && !(typeof field === 'string' && field) && typeof field !== 'function') {
      fail(`status.${key} must be a SQL expression or a function of the data context`);
    }
  }
  if (filter.scope && !['andIdIn', 'replaceSource'].includes(filter.scope)) {
    fail(`filter.scope must be 'andIdIn' or 'replaceSource', not '${filter.scope}'`);
  }

  const names = new Set();
  for (const level of table) {
    const at = `level '${level.level}'`;
    if (!level.level || !level.titleKey) fail(`every level needs level and titleKey (${JSON.stringify(level.level)})`);
    if (names.has(level.level)) fail(`${at} is defined twice`);
    names.add(level.level);
    if (!level.sql && (!level.id || !level.title)) fail(`${at} needs id and title (or sql)`);
    if (level.partitionBy && level.value && level.value !== 'window') {
      fail(`${at}: partitionBy is a window value — it cannot be combined with value '${level.value}'`);
    }
    const f = level.filter;
    if (!f) continue;
    const keys = f.keys ?? filter.keys;
    if (!level.filterSql && (!keys || !(f.table ?? filter.table))) {
      fail(`${at} has a generated filter but no filter table/keys (chart filter.table/keys or the level's own)`);
    }
    if (f.scoped && !filter.scope) fail(`${at} is filter.scoped but the chart has no filter.scope`);
    if (f.scoped && filter.scope === 'andIdIn' && !level.filterSql && keys.includes(',')) {
      fail(`${at}: filter.scope 'andIdIn' needs a single key column`);
    }
    if (f.label !== undefined && typeof f.label !== 'function') {
      fail(`${at}: filter.label must be a function of the select context`);
    }
    for (const entry of f.where ?? []) {
      if (typeof entry === 'function') continue;
      const from = Array.isArray(entry) ? entry[1] : undefined;
      const ok = ROW_SOURCES.includes(from) || (from && typeof from === 'object' && names.has(from.drill));
      if (!Array.isArray(entry) || !entry[0] || !ok) {
        fail(`${at}: filter.where entries are [column, 'id' | 'parentId' | 'type' | { drill: '<shallower level>' }] or functions`);
      }
    }
  }
}
